﻿package pl.zar.tikthrow.ui.screen

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.spring
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.outlined.CameraAlt
import androidx.compose.material.icons.outlined.Groups
import androidx.compose.material.icons.outlined.MusicNote
import androidx.compose.material.icons.outlined.PlayCircle
import androidx.compose.material.icons.outlined.Tag
import androidx.compose.material.icons.automirrored.outlined.Chat
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.scale
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.Lifecycle
import androidx.lifecycle.compose.LifecycleEventEffect
import androidx.lifecycle.viewmodel.compose.viewModel
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import pl.zar.tikthrow.shared.domain.Platform
import pl.zar.tikthrow.shared.domain.SocialMediaAccount
import pl.zar.tikthrow.shared.domain.getDisplayNameOrHandle
import pl.zar.tikthrow.ui.media.ImageFromUri
import pl.zar.tikthrow.ui.theme.*
import pl.zar.tikthrow.viewmodel.AccountsViewModel
import tikthrow.composeapp.generated.resources.*

// ── Brand colours ──────────────────────────────────────────────────────

private data class PlatformStyle(
    val accent: Color,
    val gradientColors: List<Color>,
    val drawableRes: DrawableResource,
    val fallbackIcon: ImageVector,
)

private val platformStyles: Map<Platform, PlatformStyle> = mapOf(
    Platform.TWITTER to PlatformStyle(
        accent = Color(0xFFFFFFFF),
        gradientColors = listOf(Color(0xFF1A1A1A), Color(0xFF2D2D2D)),
        drawableRes = Res.drawable.ic_twitter,
        fallbackIcon = Icons.Outlined.Tag,
    ),
    Platform.INSTAGRAM to PlatformStyle(
        accent = Color(0xFFE1306C),
        gradientColors = listOf(Color(0xFFF58529), Color(0xFFDD2A7B), Color(0xFF8134AF), Color(0xFF515BD4)),
        drawableRes = Res.drawable.ic_instagram,
        fallbackIcon = Icons.Outlined.CameraAlt,
    ),
    Platform.FACEBOOK to PlatformStyle(
        accent = Color(0xFF1877F2),
        gradientColors = listOf(Color(0xFF1877F2), Color(0xFF42A5F5)),
        drawableRes = Res.drawable.ic_facebook,
        fallbackIcon = Icons.Outlined.Groups,
    ),
    Platform.TIKTOK to PlatformStyle(
        accent = Color(0xFF00F2EA),
        gradientColors = listOf(Color(0xFFFF0050), Color(0xFF00F2EA)),
        drawableRes = Res.drawable.ic_tiktok,
        fallbackIcon = Icons.Outlined.MusicNote,
    ),
    Platform.YOUTUBE to PlatformStyle(
        accent = Color(0xFFFF0000),
        gradientColors = listOf(Color(0xFFFF0000), Color(0xFFCC0000)),
        drawableRes = Res.drawable.ic_youtube,
        fallbackIcon = Icons.Outlined.PlayCircle,
    ),
    Platform.DISCORD to PlatformStyle(
        accent = Color(0xFF5865F2),
        gradientColors = listOf(Color(0xFF5865F2), Color(0xFF7B8CFF)),
        drawableRes = Res.drawable.ic_discord,
        fallbackIcon = Icons.AutoMirrored.Outlined.Chat,
    ),
)

private val gradientBrush = Brush.linearGradient(listOf(GradientStart, GradientEnd))

// ── Main screen ────────────────────────────────────────────────────────

@Composable
fun AccountsScreen(
    modifier: Modifier = Modifier,
    viewModel: AccountsViewModel = viewModel { AccountsViewModel() }
) {
    val accounts by viewModel.accounts.collectAsState()
    val youtubeStatusMessage by viewModel.youtubeStatusMessage.collectAsState()
    val tiktokStatusMessage by viewModel.tiktokStatusMessage.collectAsState()
    val metaStatusMessage by viewModel.metaStatusMessage.collectAsState()
    var addingForPlatform by remember { mutableStateOf<Platform?>(null) }
    var oauthError by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        viewModel.refreshYouTubeStatus()
        viewModel.refreshTikTokStatus()
        viewModel.refreshMetaStatus()
    }

    LifecycleEventEffect(Lifecycle.Event.ON_RESUME) {
        viewModel.refreshTikTokStatus()
        viewModel.refreshYouTubeStatus()
        viewModel.refreshMetaStatus()
    }

    TabScreenBackground(modifier = modifier) {
        LazyColumn(
            modifier = Modifier.fillMaxSize(),
            contentPadding = PaddingValues(bottom = 24.dp),
        ) {
            // ── Header ──
            item {
                Column(modifier = Modifier.padding(start = 20.dp, end = 20.dp, top = 20.dp, bottom = 8.dp)) {
                    Text(
                        text = "Połączone konta",
                        style = TextStyle(
                            brush = gradientBrush,
                            fontSize = 28.sp,
                            fontWeight = FontWeight.Black,
                            letterSpacing = (-0.5).sp,
                        )
                    )
                    Spacer(Modifier.height(4.dp))
                    Text(
                        text = "Zarządzaj kontami w sieciach społecznościowych",
                        fontSize = 14.sp,
                        color = Slate500,
                        letterSpacing = (-0.2).sp,
                    )
                }
            }

            item { Spacer(Modifier.height(8.dp)) }

            // ── Platform cards ──
            items(Platform.entries) { platform ->
                val style = platformStyles[platform] ?: return@items
                PlatformCard(
                    platform = platform,
                    style = style,
                    accounts = accounts.filter { it.platform == platform },
                    statusMessage = when (platform) {
                        Platform.YOUTUBE -> youtubeStatusMessage
                        Platform.TIKTOK -> tiktokStatusMessage
                        Platform.FACEBOOK -> metaStatusMessage
                        Platform.INSTAGRAM -> metaStatusMessage
                        else -> null
                    },
                    onAddClick = { addingForPlatform = platform },
                    onRemove = { viewModel.removeAccount(it) }
                )
            }
        }
    }

    addingForPlatform?.let { platform ->
        val style = platformStyles[platform] ?: return@let
        if (platform == Platform.YOUTUBE) {
            OAuthLoginDialog(
                platform = Platform.YOUTUBE,
                style = style,
                error = oauthError,
                onLogin = {
                    oauthError = null
                    if (viewModel.startYouTubeLogin()) {
                        addingForPlatform = null
                    } else {
                        oauthError = "Nie udało się otworzyć przeglądarki"
                    }
                },
                onDismiss = {
                    oauthError = null
                    addingForPlatform = null
                }
            )
        } else if (platform == Platform.TIKTOK) {
            OAuthLoginDialog(
                platform = Platform.TIKTOK,
                style = style,
                error = oauthError,
                onLogin = {
                    oauthError = null
                    if (viewModel.startTikTokLogin()) {
                        addingForPlatform = null
                    } else {
                        oauthError = "Nie udało się otworzyć przeglądarki"
                    }
                },
                onDismiss = {
                    oauthError = null
                    addingForPlatform = null
                }
            )
        } else if (platform == Platform.FACEBOOK || platform == Platform.INSTAGRAM) {
            OAuthLoginDialog(
                platform = Platform.FACEBOOK,
                style = style,
                error = oauthError,
                onLogin = {
                    oauthError = null
                    if (viewModel.startMetaLogin()) {
                        addingForPlatform = null
                    } else {
                        oauthError = "Nie udało się otworzyć przeglądarki"
                    }
                },
                onDismiss = {
                    oauthError = null
                    addingForPlatform = null
                }
            )
        } else {
            AddAccountDialog(
                platform = platform,
                style = style,
                onConfirm = { handle, displayName ->
                    viewModel.addAccount(platform, handle, displayName)
                    addingForPlatform = null
                },
                onDismiss = { addingForPlatform = null }
            )
        }
    }
}

// ── Platform card ──────────────────────────────────────────────────────

@Composable
private fun PlatformCard(
    platform: Platform,
    style: PlatformStyle,
    accounts: List<SocialMediaAccount>,
    statusMessage: String?,
    onAddClick: () -> Unit,
    onRemove: (String) -> Unit
) {
    val hasAccounts = accounts.isNotEmpty()

    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 6.dp),
        shape = RoundedCornerShape(8.dp),
        color = Slate100,
        tonalElevation = 0.dp,
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            // ── Top row: logo + name + add button ──
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(14.dp)
                ) {
                    // Platform logo icon with gradient background
                    Box(
                        modifier = Modifier
                            .size(46.dp)
                            .clip(RoundedCornerShape(6.dp))
                            .background(
                                Brush.linearGradient(style.gradientColors)
                            ),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            painter = painterResource(style.drawableRes),
                            contentDescription = platform.displayName,
                            modifier = Modifier.size(22.dp),
                            tint = Color.White
                        )
                    }

                    Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
                        Text(
                            text = platform.displayName,
                            fontSize = 17.sp,
                            fontWeight = FontWeight.Bold,
                            color = Slate900,
                            letterSpacing = (-0.3).sp,
                        )
                        Text(
                            text = if (hasAccounts) {
                                "${accounts.size} konto${accountSuffix(accounts.size)}"
                            } else {
                                "Brak kont"
                            },
                            fontSize = 12.sp,
                            color = if (hasAccounts) style.accent else Slate400,
                            fontWeight = if (hasAccounts) FontWeight.SemiBold else FontWeight.Normal,
                            letterSpacing = (-0.1).sp,
                        )
                    }
                }

                // Add button
                val interactionSource = remember { MutableInteractionSource() }
                val isPressed by interactionSource.collectIsPressedAsState()
                val scale by animateFloatAsState(
                    targetValue = if (isPressed) 0.92f else 1f,
                    animationSpec = spring(stiffness = Spring.StiffnessHigh)
                )

                Surface(
                    onClick = onAddClick,
                    interactionSource = interactionSource,
                    shape = RoundedCornerShape(4.dp),
                    color = Color.Transparent,
                    modifier = Modifier.scale(scale),
                ) {
                    Box(
                        modifier = Modifier
                            .clip(RoundedCornerShape(4.dp))
                            .background(
                                Brush.linearGradient(
                                    style.gradientColors.map { it.copy(alpha = 0.18f) }
                                )
                            )
                            .padding(horizontal = 14.dp, vertical = 9.dp),
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(5.dp)
                        ) {
                            Icon(
                                Icons.Default.Add,
                                contentDescription = null,
                                tint = style.accent,
                                modifier = Modifier.size(15.dp)
                            )
                            Text(
                                text = "Dodaj",
                                fontSize = 13.sp,
                                fontWeight = FontWeight.Bold,
                                color = style.accent,
                                letterSpacing = (-0.2).sp,
                            )
                        }
                    }
                }
            }

            // ── Connected accounts list ──
            AnimatedVisibility(
                visible = hasAccounts,
                enter = expandVertically(spring(stiffness = Spring.StiffnessMediumLow)) + fadeIn(),
                exit = shrinkVertically(spring(stiffness = Spring.StiffnessMediumLow)) + fadeOut(),
            ) {
                Column {
                    Spacer(Modifier.height(14.dp))
                    HorizontalDivider(
                        color = Slate200,
                        thickness = 1.dp,
                    )

                    accounts.forEach { account ->
                        AccountRow(
                            account = account,
                            style = style,
                            onRemove = { onRemove(account.id) },
                        )
                    }
                }
            }

            // ── OAuth status message ──
            if (statusMessage != null && platform in listOf(Platform.YOUTUBE, Platform.TIKTOK, Platform.FACEBOOK, Platform.INSTAGRAM)) {
                Spacer(Modifier.height(12.dp))
                Surface(
                    shape = RoundedCornerShape(4.dp),
                    color = style.accent.copy(alpha = 0.08f),
                ) {
                    Text(
                        text = statusMessage,
                        modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp),
                        fontSize = 12.sp,
                        color = if (hasAccounts) style.accent else Slate500,
                        fontWeight = FontWeight.SemiBold,
                        letterSpacing = (-0.1).sp,
                    )
                }
            }
        }
    }
}

// ── Account row ────────────────────────────────────────────────────────

@Composable
private fun AccountRow(
    account: SocialMediaAccount,
    style: PlatformStyle,
    onRemove: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = 12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            AccountAvatar(account = account, style = style)

            Column(verticalArrangement = Arrangement.spacedBy(1.dp)) {
                val isOAuthAccount = account.platform == Platform.YOUTUBE ||
                    account.platform == Platform.TIKTOK ||
                    account.platform == Platform.FACEBOOK ||
                    account.platform == Platform.INSTAGRAM
                Text(
                    text = if (isOAuthAccount) (account.displayName ?: account.accountHandle) else "@${account.accountHandle}",
                    fontSize = 14.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = Slate900,
                    letterSpacing = (-0.2).sp,
                )
                if (!isOAuthAccount) {
                    account.displayName?.let {
                        Text(
                            text = it,
                            fontSize = 12.sp,
                            color = Slate500,
                            letterSpacing = (-0.1).sp,
                        )
                    }
                }
            }
        }

        IconButton(
            onClick = onRemove,
            modifier = Modifier.size(32.dp),
        ) {
            Icon(
                Icons.Default.Delete,
                contentDescription = "Usuń",
                tint = Slate400,
                modifier = Modifier.size(16.dp)
            )
        }
    }
}

// ── OAuth login dialog (YouTube & TikTok) ─────────────────────────────

@Composable
private fun AccountAvatar(
    account: SocialMediaAccount,
    style: PlatformStyle,
) {
    val avatarUrl = account.avatarUrl
    Box(
        modifier = Modifier
            .size(36.dp)
            .clip(CircleShape)
            .background(Brush.linearGradient(style.gradientColors)),
        contentAlignment = Alignment.Center
    ) {
        if (avatarUrl != null) {
            ImageFromUri(
                uri = avatarUrl,
                isVideo = false,
                modifier = Modifier.matchParentSize()
            )
        } else {
            Text(
                text = account.getDisplayNameOrHandle().take(1).uppercase(),
                fontSize = 14.sp,
                fontWeight = FontWeight.ExtraBold,
                color = Color.White
            )
        }
    }
}

@Composable
private fun OAuthLoginDialog(
    platform: Platform,
    style: PlatformStyle,
    error: String?,
    onLogin: () -> Unit,
    onDismiss: () -> Unit
) {
    val description = when (platform) {
        Platform.TIKTOK -> "Połącz konto przez oficjalne logowanie TikTok. Po autoryzacji wrócisz automatycznie do aplikacji."
        Platform.FACEBOOK, Platform.INSTAGRAM ->
            "Połącz Facebook i Instagram przez wspólne logowanie Meta. Po autoryzacji strony FB i konta IG zostaną automatycznie połączone."
        else -> "Połącz konto przez oficjalne logowanie Google. Po zgodzie wrócisz do aplikacji przez callback serwera."
    }
    val buttonLabel = when (platform) {
        Platform.TIKTOK -> "Zaloguj przez TikTok"
        Platform.FACEBOOK, Platform.INSTAGRAM -> "Zaloguj przez Meta"
        else -> "Zaloguj przez YouTube"
    }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                Box(
                    modifier = Modifier
                        .size(36.dp)
                        .clip(RoundedCornerShape(4.dp))
                        .background(Brush.linearGradient(style.gradientColors)),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        painter = painterResource(style.drawableRes),
                        contentDescription = null,
                        modifier = Modifier.size(18.dp),
                        tint = Color.White
                    )
                }
                Text(
                    platform.displayName,
                    fontWeight = FontWeight.ExtraBold,
                    color = Slate900,
                    fontSize = 18.sp,
                    letterSpacing = (-0.3).sp,
                )
            }
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text(text = description, color = Slate600, fontSize = 14.sp)
                error?.let {
                    Text(
                        text = it,
                        color = MaterialTheme.colorScheme.error,
                        fontSize = 13.sp,
                        fontWeight = FontWeight.SemiBold
                    )
                }
            }
        },
        confirmButton = {
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(4.dp))
                    .background(Brush.linearGradient(style.gradientColors))
                    .clickable { onLogin() }
                    .padding(horizontal = 20.dp, vertical = 11.dp)
            ) {
                Text(buttonLabel, color = Color.White, fontWeight = FontWeight.Bold)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text("Anuluj", color = Slate500) }
        },
        containerColor = Slate100,
        titleContentColor = Slate900,
        shape = RoundedCornerShape(8.dp)
    )
}

// ── Add account dialog ─────────────────────────────────────────────────

@Composable
private fun AddAccountDialog(
    platform: Platform,
    style: PlatformStyle,
    onConfirm: (String, String?) -> Unit,
    onDismiss: () -> Unit
) {
    var handle by remember { mutableStateOf("") }
    var displayName by remember { mutableStateOf("") }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                Box(
                    modifier = Modifier
                        .size(36.dp)
                        .clip(RoundedCornerShape(4.dp))
                        .background(Brush.linearGradient(style.gradientColors)),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        painter = painterResource(style.drawableRes),
                        contentDescription = null,
                        modifier = Modifier.size(18.dp),
                        tint = Color.White
                    )
                }
                Text(
                    platform.displayName,
                    fontWeight = FontWeight.ExtraBold,
                    color = Slate900,
                    fontSize = 18.sp,
                    letterSpacing = (-0.3).sp,
                )
            }
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                OutlinedTextField(
                    value = handle,
                    onValueChange = { handle = it },
                    label = { Text("Nazwa użytkownika", color = Slate500) },
                    placeholder = { Text("np. johndoe", color = Slate400) },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = style.accent,
                        unfocusedBorderColor = Slate200,
                        focusedTextColor = Slate900,
                        unfocusedTextColor = Slate900,
                        cursorColor = style.accent,
                        focusedContainerColor = Slate100,
                        unfocusedContainerColor = Slate100,
                    ),
                    shape = RoundedCornerShape(6.dp)
                )
                OutlinedTextField(
                    value = displayName,
                    onValueChange = { displayName = it },
                    label = { Text("Wyświetlana nazwa (opcjonalnie)", color = Slate500) },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = style.accent,
                        unfocusedBorderColor = Slate200,
                        focusedTextColor = Slate900,
                        unfocusedTextColor = Slate900,
                        cursorColor = style.accent,
                        focusedContainerColor = Slate100,
                        unfocusedContainerColor = Slate100,
                    ),
                    shape = RoundedCornerShape(6.dp)
                )
            }
        },
        confirmButton = {
            val canConfirm = handle.isNotBlank()
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(4.dp))
                    .background(
                        if (canConfirm) Brush.linearGradient(style.gradientColors)
                        else Brush.linearGradient(listOf(Slate200, Slate200))
                    )
                    .clickable(enabled = canConfirm) { onConfirm(handle, displayName.ifBlank { null }) }
                    .padding(horizontal = 20.dp, vertical = 11.dp)
            ) {
                Text(
                    "Dodaj",
                    color = if (canConfirm) Color.White else Slate400,
                    fontWeight = FontWeight.Bold
                )
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text("Anuluj", color = Slate500) }
        },
        containerColor = Slate100,
        titleContentColor = Slate900,
        shape = RoundedCornerShape(8.dp)
    )
}

private fun accountSuffix(count: Int) = when {
    count == 1 -> ""
    else -> "a"
}
